import React, { useEffect } from 'react'
import { Button } from 'rsuite'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { userSelector } from '../../../features/user/userSlice'
import { postUserRoomCreate } from '../../../api/rest'
import { envSelector } from '../../../features/environment/environmentSlice'
import { navigatePage } from '../../../misc/utils/navigation'
import { CreateRoom, RoomType } from '../../../misc/domain/room'

// Vodafone Shops private-label center panel (design approved 26/07/2026 — see the
// "Iris — Vodafone Shops Home" artifact). Same call flow as InCallMain; only branding differs.
// All vertical sizes scale with clamp()/vh so the panel fits shop PCs (1366×768 ≈ 650px of
// usable viewport) without clipping — the WrapperPage is 100vh with overflow hidden.

const featIconStyle: React.CSSProperties = {
  width: 'clamp(28px, 5vh, 42px)', height: 'clamp(28px, 5vh, 42px)',
  stroke: '#63B0B4', fill: 'none', strokeWidth: 1.7
}

const featTitleStyle: React.CSSProperties = {
  color: 'white', fontSize: 'clamp(13px, 2.2vh, 16.5px)', fontWeight: 600, marginTop: 4
}

const featBodyStyle: React.CSSProperties = {
  color: '#B7CBCE', fontSize: 'clamp(11px, 1.8vh, 14px)', marginTop: 2, lineHeight: 1.4
}

const InCallMainVodafone: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(envSelector)
  const { room } = useAppSelector(userSelector)

  const createRoomRequest = () => {
    if (user && !room) {
      // Room has not been requested
      let newRoom: CreateRoom = { user_id: user.uuid, is_emergency: false, invite_users: [], room_type: RoomType.SIMPLE }
      dispatch(postUserRoomCreate(newRoom))
    }
  }

  // Upon room request completion navigate to room
  useEffect(() => {
    if (room) {
      const { id } = room
      navigatePage(dispatch, navigate, `/rooms/${id}`, { state: { room } })
    }
  }, [room, dispatch, navigate])

  return (
    <div style={{
      width: '100%', height: '100vh', textAlign: 'center', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column',
      padding: 'clamp(10px, 2.5vh, 34px) 6% clamp(6px, 1.5vh, 16px)',
      background: 'radial-gradient(120% 90% at 50% -10%, #24424a 0%, #1F3539 55%)',
      fontFamily: 'Alegreya Sans'
    }}>
      <p style={{ color: 'white', fontSize: 'clamp(19px, 3.2vh, 29px)', fontWeight: 500 }}>
        Πώς μπορούμε να σας εξυπηρετήσουμε;
      </p>
      <p style={{ color: '#B7CBCE', fontSize: 'clamp(12.5px, 2vh, 16.5px)', marginTop: 'clamp(4px, 1vh, 10px)' }}>
        Συνδεθείτε τώρα με διερμηνέα στη Νοηματική Γλώσσα ή μέσω γραπτού μηνύματος.
      </p>

      <div style={{
        backgroundColor: 'rgba(36, 64, 74, .75)', borderRadius: 18,
        margin: 'clamp(10px, 2.8vh, 28px) auto 0', width: '100%',
        maxWidth: 760, padding: 'clamp(12px, 2.8vh, 30px) clamp(20px, 4vw, 40px) clamp(10px, 2.2vh, 24px)'
      }}>
        <img src="/img/vodafone/logo-vertical.png" alt="Vodafone"
             style={{ width: 'clamp(110px, 20vh, 210px)', display: 'block', margin: '0 auto' }}/>
        <p style={{ color: 'white', fontSize: 'clamp(14px, 2.2vh, 19px)', marginTop: 'clamp(4px, 1vh, 8px)' }}>
          Είμαστε εδώ για να σας βοηθήσουμε.
        </p>
        <hr style={{ border: 0, borderTop: '1px solid rgba(255,255,255,.12)', margin: 'clamp(10px, 2.4vh, 24px) 0 clamp(8px, 2vh, 20px)' }}/>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: '0 28px', alignItems: 'start' }}>
          <div>
            <svg style={featIconStyle} viewBox="0 0 42 42">
              <rect x="4" y="11" width="22" height="20" rx="4"/>
              <path d="M26 18l10-6v18l-10-6"/>
            </svg>
            <p style={featTitleStyle}>Άμεση σύνδεση</p>
            <p style={featBodyStyle}>Με πιστοποιημένους διερμηνείς<br/>24/7</p>
          </div>
          <div style={{ background: 'rgba(255,255,255,.12)', height: '100%' }}/>
          <div>
            <svg style={featIconStyle} viewBox="0 0 42 42">
              <path d="M21 4l13 5v10c0 9-5.5 15.5-13 19-7.5-3.5-13-10-13-19V9z"/>
              <path d="M15 21l4 4 8-9"/>
            </svg>
            <p style={featTitleStyle}>Ασφαλές &amp; ιδιωτικό</p>
            <p style={featBodyStyle}>Οι επικοινωνίες σας είναι<br/>προστατευμένες</p>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 'clamp(10px, 2.6vh, 26px)' }}>
        <Button size={'lg'} appearance={'primary'} style={{ backgroundColor: '#3B888C' }} onClick={createRoomRequest}>
          <img src="/img/icons/call.svg" alt=""/>
          <span style={{ paddingLeft: 10 }}>Καλέστε μας</span>
        </Button>
      </div>

      {/* Bottom info block pinned by the flex column: never overlaps, never clips. */}
      <div style={{ marginTop: 'auto', paddingTop: 'clamp(6px, 1.4vh, 16px)' }}>
        <p style={{ color: '#B7CBCE', fontSize: 'clamp(11px, 1.8vh, 14px)' }}>
          Υπηρεσία προσφέρεται δωρεάν από τη Vodafone για κωφούς και βαρήκοους πελάτες.
        </p>
        <p style={{ color: 'white', fontSize: 'clamp(12px, 1.9vh, 15px)', marginTop: 2 }}>
          Για τεχνική υποστήριξη: <span style={{ color: '#63B0B4', fontWeight: 600 }}>13840</span>
        </p>
      </div>
    </div>
  )
}

export default InCallMainVodafone
